import { encode } from 'blurhash';

// Encoding reads every pixel once per component, so it runs on a small copy:
// 64x36 hashes in a few milliseconds, where a full-size photo takes seconds and
// gives essentially the same result.
const ENCODE_WIDTH = 64;
const ENCODE_HEIGHT = 36;

// 4x3 is BlurHash's suggested balance for landscape images.
const COMPONENTS_X = 4;
const COMPONENTS_Y = 3;

// A BlurHash placeholder for a 16:9 thumbnail, from the file being uploaded.
//
// Computed from the local file rather than the uploaded URL: reading pixels
// back from the asset domain would need it to send CORS headers. The image is
// cropped to its middle 16:9, the part thumbnails show, so the placeholder
// matches what replaces it.
//
// Returns null rather than throwing: a missing placeholder shouldn't stop the
// upload it's for.
export async function blurHashForThumbnail(file: File): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(file);

    const canvas = document.createElement('canvas');
    canvas.width = ENCODE_WIDTH;
    canvas.height = ENCODE_HEIGHT;

    const context = canvas.getContext('2d');

    if (!context) {
      bitmap.close();
      return null;
    }

    // Covers the canvas, overflowing evenly on whichever axis is too long.
    const scale = Math.max(ENCODE_WIDTH / bitmap.width, ENCODE_HEIGHT / bitmap.height);
    const drawWidth = bitmap.width * scale;
    const drawHeight = bitmap.height * scale;

    context.drawImage(
      bitmap,
      (ENCODE_WIDTH - drawWidth) / 2,
      (ENCODE_HEIGHT - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    bitmap.close();

    const { data } = context.getImageData(0, 0, ENCODE_WIDTH, ENCODE_HEIGHT);

    return encode(data, ENCODE_WIDTH, ENCODE_HEIGHT, COMPONENTS_X, COMPONENTS_Y);
  } catch {
    return null;
  }
}
